/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService, BreadcrumbService, NotificationService, SaveAndQueryComponent, StatusService, wrapBusy } from '@core';
import {
  Account,
  AccountAddress,
  AccountProcurementCard,
  AccountPurchaseOrder,
  ActiveStatus,
  CollectionScopeEnum,
  ActiveStatusEnum,
  ApprovalTree,
  ApprovalTreeUserGroup,
  BillingConfigResolver,
  DocSubmitTypeEnum,
  IAddress,
  KitTag,
  Program,
  ProgramAllowance,
  ProgramAllowanceAddonException,
  ProgramAllowanceFeatureException,
  ProgramAllowanceRemainderRuleEnum,
  ProgramAllowanceUserGroupMap,
  ProgramBillingConfig,
  ProgramBudget,
  GuidelineProgram,
  ProgramIssuance,
  ProgramIssuanceUserGroupMap,
  ProgramPcardTypeEnum,
  ProgramProductCategoryTag,
  ProgramProductCategoryTagMap,
  ProgramProductTypeConfig,
  ProgramProductTypeTag,
  ProgramPurchaseOrderTypeEnum,
  ProgramRapidTemplate,
  ProgramRapidTemplateProductTypeConfig,
  ProgramStartDateTypeEnum,
  ProgramSupplier,
  ProgramTypeEnum,
  ProgramUserGroup,
  ProgramView,
  ProgramViewProductTypeConfig,
  ProgramViolationRuleEnum,
  ProximityRightEnum,
  SupplierAccount,
} from '@models';

import { EntityFns } from '@data';
import { DateFns, UtilFns } from '@utils';
import { Entity, EntityError } from 'breeze-client';
import { Guid } from 'guid-typescript';
import * as _ from 'lodash';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApprovalTreeUserGroupDialogComponent } from '../approval-trees/approval-tree-user-group.dialog.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ProgramMenuService } from '../services/program-menu.service';
import { IProgramNewCycleData } from './program-cycle.dialog.component';
import { ProgramChangeEndDateDialogComponent } from './program-change-end-date.dialog.component';
import { AddressValidationService } from 'libs/core/src/lib/services/address-validation.service';
import { ProgramProductTypeConfigsSubComponent } from './program-product-type-configs.sub.component';
import { ProgramUserGroupsSubComponent } from './program-user-groups.sub.component';
import { ProgramGuidelinesSubComponent } from './program-guidelines.sub.component';

enum ViewEnum {
  None,
  Info,
  UserGroups,
  ProductTypeConfigs,
  ProductTags,
  MenuCategories,
  ProductViews,
  Issuances,
  Allowances,
  RapidTemplates,
  Kits,
  Budgets,
  Billing,
  Suppliers,
  Cancellation,
  Communications,
  // Throwaway: one-shot Program Product Reconcile feature. Remove this enum value
  // and its menu/getViewTitle/template wiring when the cleanup work is finished.
  Reconcile,
  PugTransfer,
  ShippingTransfer,
  Guidelines,
}

export interface ISubComponent {
  visible: boolean;
  refresh: (forceRefresh: boolean) => Promise<void>;
  markError?: (errEnt: Entity, propName?: string) => void;
}

interface IComponentNameMap {
  [index: string]: ISubComponent;
}

interface INewProgramType {
  programType: string;
  availableAfterJoinDays: number;
  startDate: Date;
  endDate: Date;
  durationInDays: number;
}

// TODO: handle changes to productTypeConfigs and programProductTags in other subcomponents.

@Component({
  selector: 'prox-program-frm',
  templateUrl: './program-frm.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [],
})
export class ProgramFrmComponent extends SaveAndQueryComponent implements OnDestroy {
  @ViewChildren('programSub') subComponents!: QueryList<ISubComponent>;
  @ViewChild(ProgramUserGroupsSubComponent) pugSub?: ProgramUserGroupsSubComponent;
  @ViewChild(ProgramGuidelinesSubComponent) guidelinesSub?: ProgramGuidelinesSubComponent;

  // For binding purposes
  // ProgramStartDateTypeEnum = ProgramStartDateTypeEnum;
  ActiveStatusEnum = ActiveStatusEnum;

  ViewEnum = ViewEnum;
  componentNameMap: IComponentNameMap = {};
  activeView = ViewEnum.Info;
  accountId!: string;
  incomingProgramId!: string;
  account!: Account;
  program!: Program;

  allAccountTags: ProgramProductTypeTag[] = [];
  allKitTags: KitTag[] = [];
  // Populated for Managed Inventory programs only. Feeds ProgramInfoSubComponent's
  // Managed Distribution linkage grid.
  mdSuppliersForInventory: ProgramSupplier[] = [];
  private authCanEdit = false;
  isBeingAdded!: boolean;
  pptts: ProgramProductTypeTag[] = [];
  kts: KitTag[] = [];

  items: MenuItem[] = [];
  items2: MenuItem[] = [];
  activatingStatusCode?: ActiveStatusEnum | null;
  newProgramType!: INewProgramType;
  billingAddresses: AccountAddress[] = [];
  pos: AccountPurchaseOrder[] = [];
  pcards: AccountProcurementCard[] = [];

  // programProductTypeConfigs: ProgramProductTypeConfig[] = [];

  constructor(
    baseService: BaseService,
    override route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,

    private statusService: StatusService,
    public pngDialogService: DialogService,
    private addressValidationService: AddressValidationService,
    private programMenuService: ProgramMenuService,
    private notificationService: NotificationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  /** If EDIT mode, get the entity from dbQueryService; if ADD mode, call createEntity() */
  override async updateFromParams(params: object): Promise<void> {
    this.newProgramType = {
      programType: '',
      availableAfterJoinDays: 0,
      durationInDays: 0,
      startDate: new Date(),
      endDate: new Date(),
    };

    this.route.queryParams.subscribe((params) => {
      this.newProgramType.programType = params['programType'];
      this.newProgramType.availableAfterJoinDays = params['availableAfterJoinDays'];
      this.newProgramType.durationInDays = params['durationInDays'];
      this.newProgramType.startDate = params['startDate'];
      this.newProgramType.endDate = params['endDate'];
    });

    const h = this.newProgramType;

    this.accountId = params['accountId'];
    this.incomingProgramId = params['programId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    UtilFns.assertNonEmptyString(this.incomingProgramId, 'programId');
    this.activatingStatusCode = this.route.snapshot.queryParams['activatingStatusCode'];

    this.isBeingAdded = this.incomingProgramId === 'add';
    if (this.isBeingAdded) {
      this.program = this.createNewProgram();
      if (this.newProgramType.programType === 'ff') {
        this.program.programStartDateTypeId = ProgramStartDateTypeEnum.FixedStartAndEndDate;
        this.program.availableAfterJoinDateDays = this.newProgramType.availableAfterJoinDays;
        this.program.startDate = this.newProgramType.startDate;
        this.program.endDate = this.newProgramType.endDate;
      }

      if (this.newProgramType.programType === 'vv') {
        this.program.programStartDateTypeId = ProgramStartDateTypeEnum.VariableStartAndEndDate;
        this.program.availableAfterJoinDateDays = this.newProgramType.availableAfterJoinDays;
        this.program.durationDays = this.newProgramType.durationInDays;
      }

      if (this.newProgramType.programType === 'vf') {
        this.program.programStartDateTypeId = ProgramStartDateTypeEnum.VariableStartFixedEndDate;
        this.program.availableAfterJoinDateDays = this.newProgramType.availableAfterJoinDays;
        this.program.endDate = this.newProgramType.endDate;
      }

      if (this.newProgramType.programType === 'inv') {
        this.program.programStartDateTypeId = ProgramStartDateTypeEnum.FixedStartAndEndDate;
        this.program.availableAfterJoinDateDays = 0;
        this.program.startDate = this.newProgramType.startDate;
        this.program.endDate = this.newProgramType.endDate;
      }

      // it was 'add' coming into this.
    } else {
      this.program = await this.dbQueryService.getProgramById(this.incomingProgramId);
    }

    this.buildMainMenu();

    UtilFns.assertNonNull(this.authUser);
    this.authCanEdit = this.authUser.hasRight(ProximityRightEnum.CanEditProgram);

    const [account, pptts, kts, billingAddresses, pos, pcards] = await Promise.all([
      this.dbQueryService.getAccountById(this.accountId),
      this.dbQueryService.getProgramProductTypeTagsForAccount(this.program.accountId),
      this.dbQueryService.getKitTagsForAccount(this.program.accountId),
      this.dbQueryService.getBillingAccountAddresses(this.program.accountId),
      this.dbQueryService.getAccountPurchaseOrders(this.program.accountId),
      this.dbQueryService.getAccountProcurementCards(this.program.accountId),

      // we don't need to join to them anywhere - just retrieve them into cache for later
      // this.dbQueryService.getProgramProductTypeConfigsByProgram(this.program.id),
      // this.dbQueryService.getProgramUserGroupsForApprovalTree(this.program.approvalTreeId!),
      // this.dbQueryService.getProgramKitsByProgramId(this.program.id),
    ]);

    // Only query program-specific data if editing an existing program
    if (!this.isBeingAdded) {
      await Promise.all([
        this.dbQueryService.getProgramProductTypeConfigsByProgram(this.program.id),
        this.dbQueryService.getProgramUserGroupsForApprovalTree(this.program.approvalTreeId!),
        this.dbQueryService.getProgramKitsByProgramId(this.program.id),
      ]);
    }

    // For Managed Inventory programs, preload the MD-program-supplier list to feed the
    // Managed Distribution linkage grid on the Information sub-page.
    if (this.program.programTypeId === ProgramTypeEnum.Inventory) {
      this.mdSuppliersForInventory = await this.dbQueryService.getManagedProgramSuppliersForAccount(this.program.accountId);
    }

    // Don't think that this is needed anymore..

    // this.activationService.deactivate();

    this.account = account;
    this.pptts = pptts;
    this.allAccountTags = pptts;
    this.allKitTags = kts;
    this.billingAddresses = billingAddresses;
    this.pos = pos;
    this.pcards = pcards;

    this.setTitle('Proximity Program for ' + this.account.name);

    this.breadcrumbService.push({ label: 'Programs for Account: ' + this.account.name });

    // This is to handle the case where the program list is activating the program.
    // If it succeeds then we navigate back to the list.
    // other we leave the user here to fix the errors.
    if (this.activatingStatusCode != null) {
      const ok = await this.tryProgramActivate();
      if (ok) {
        this.navigateBack();
        return;
      }
    }
    this.isPageReady = true;
  }

  isManaged() {
    return this.program.programTypeId == ProgramTypeEnum.Managed;
  }

  /** Feeds the Change Log button, which the mixin hides unless a form names its entity. The
   *  markup for it has always been here, but without this override `canShowLog()` read the
   *  mixin's base `entity` — undefined — so the button never rendered and program history was
   *  reachable only by hand-written SQL (Jeff, 2026-09-07).
   *
   *  It shows more than edits to the Program row itself. ChangeLogGridComponent queries
   *  `entityId OR entityId2`, and a ProgramProductTypeConfig is keyed (ProgramId,
   *  ProductTypeConfigId), so attaching or removing a product shows up here too.
   *
   *  `program` is declared with a definite-assignment `!` but is genuinely unset until the
   *  route resolves, and `canShowLog()` tests it truthily, so the button simply stays hidden
   *  until the program loads. */
  override get entity() {
    return this.program;
  }

  override async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {
    // needed in this component to avoid Angular change detection issues - not needed on regular wrapBusy.
    await UtilFns.wait(1);
    return super.wrapBusy(op);
  }

  public override ngOnDestroy(): void {
    this.breadcrumbService.pop();
    this.programMenuService.clearMenuItems();
  }

  @wrapBusy
  public async askProgramOnHold() {
    const ok = await this.askProceedDialog();
    if (!ok) return;

    this.program.activeStatusId = ActiveStatusEnum.Hold;
    await this.onSave();
    await this.refreshAllSubs();

    const programLink = `/account/accounts/${this.accountId}/manage/programs/${this.program.id}`;
    this.notificationService.add(
      'warn',
      'Program On Hold',
      `<a href="${programLink}">${this.program.name}</a> is on Hold. Remember to re-activate when editing is complete.`,
      undefined,
      `program-hold:${this.program.id}`,
    );
  }

  public async askProgramActivate() {
    const ok = await this.askProceedDialog();
    if (!ok) return;
    return await this.tryProgramActivate();
  }

  /**
   * Gate 1: an Inventory program distributes account-owned stock, and no supplier has agreed to
   * carry that until one of the Account's partnerships enables Account-Owned Inventory
   * Management. The permission is granted one partnership at a time but reads as an
   * account-level capability, so any one partnership carrying it enables the account.
   *
   * The same rule is enforced in BeforeSaveHandler.CheckAccountOwnedInventoryGates, because a
   * partnership can be changed between page load and this click.
   */
  public isAccountOwnedInventoryEnabled() {
    return this.account.supplierAccounts.some((sa) => sa.allowInventory);
  }

  @wrapBusy
  public async tryProgramActivate() {
    const targetStatusId = this.activatingStatusCode ?? this.ActiveStatusEnum.Active;
    if (
      targetStatusId === ActiveStatusEnum.Active &&
      this.program.programTypeId === ProgramTypeEnum.Inventory &&
      !this.isAccountOwnedInventoryEnabled()
    ) {
      this.activatingStatusCode = undefined;
      await this.dialogService.warnDialog(
        'Cannot Activate',
        'No Proximity Supplier Partnership for this Account has Account-Owned Inventory Management enabled. ' +
          'An Inventory Program may not be activated until one does.',
      );
      return false;
    }

    if (this.hasChanges()) {
      await this.onSave();
      // save didn't save all errors probably because of cross-validation errors.
      if (this.hasChanges()) return false;
    }
    this.activatingStatusCode = this.activatingStatusCode ?? this.ActiveStatusEnum.Active;

    try {
      const errEntities = await this.validateEntities();
      if (errEntities.length > 0) {
        this.toastr.info('Unable to activate - Fix the following errors first');
        await this.handleBreezeValidationErrors(errEntities);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.program.activeStatusId = this.activatingStatusCode ?? this.ActiveStatusEnum.Active;
        await this.onSave();
        this.notificationService.resolveByKey(`program-hold:${this.program.id}`);
      }
      await this.refreshAllSubs();
      return this.program.activeStatusId == ActiveStatusEnum.Active || this.program.activeStatusId == ActiveStatusEnum.Phaseout;
    } finally {
      this.activatingStatusCode = undefined;
    }
  }

  async askProceedDialog() {
    if (this.program.activeStatusId != ActiveStatusEnum.Active && this.program.activeStatusId != ActiveStatusEnum.Hold) {
      return false;
    }

    const pendingStatusId = this.program.activeStatusId == ActiveStatusEnum.Hold ? ActiveStatusEnum.Active : ActiveStatusEnum.Hold;

    const ok = await this.dialogService.verifyProceedDialog(
      'Program Status Change',
      this.statusService.getStatusExplanation(pendingStatusId, 'Program'),
    );

    return ok;
  }

  public isReadOnly() {
    return !this.authCanEdit || !(this.program.activeStatusId == ActiveStatusEnum.Hold);
  }

  statusMessage(): string {
    let msg = '';
    if (this.program.activeStatusId == ActiveStatusEnum.Active) {
      return 'Program is Active and Not Editable.  To edit, set Program status to Hold.';
    } else if (this.program.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Program is On-Hold and is Editable';
    } else {
      return 'Program is ' + this.program.activeStatus?.name + ' and is Not Editable';
    }

    if (this.activatingStatusCode != null) {
      msg = msg + ' - Activation pending clearance of validation errors.';
    }
    return msg;
  }

  @wrapBusy
  async refreshAllSubs() {
    // OLD CODE - tries to refresh all subcomponents at once.
    // const promises = this.subComponents.map((x) => x.refresh(true));
    // await Promise.all(promises);

    for (const subComponent of this.subComponents) {
      await subComponent.refresh(true);
    }
    // Every status change lands here, and Change End Date is hidden on an Inactive program.
    // Without this the menu would keep offering it until the page was reloaded.
    this.buildMainMenu();
  }

  ifEditable<T>(colDef: T) {
    return this.isReadOnly() ? ({} as T) : colDef;
  }

  // called from each subcomponent
  updateComponentNameMap(subComponent: ISubComponent) {
    if (!subComponent.visible) return;
    const name = ViewEnum[this.activeView];
    this.componentNameMap[name] = subComponent;
  }

  public buildMainMenu() {
    /*     if (!this.program!.programType) {
      this.program.programTypeId = ProgramTypeEnum.Managed;
    } */

    if (this.program.programTypeId == ProgramTypeEnum.Managed) {
      this.items = [
        {
          label: 'Information',
          icon: 'pi pi-fw pi-cog',
          command: () => {
            this.showPanel(ViewEnum.Info);
          },
        },
        {
          label: 'Supplier Settings',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Suppliers);
          },
        },
        {
          label: 'Program User Groups',
          icon: 'pi pi-fw pi-user',
          command: () => {
            this.showPanel(ViewEnum.UserGroups);
          },
        },
        {
          label: 'Products and Tags',
          icon: 'fa-duotone fa-regular fa-tags',
          command: () => {
            this.showPanel(ViewEnum.ProductTypeConfigs);
          },
        },
        {
          label: 'Kits',
          icon: 'fa-duotone fa-regular fa-suitcase',
          command: () => {
            this.showPanel(ViewEnum.Kits);
          },
        },
        {
          label: 'Menu Categories',
          icon: 'fa-duotone fa-solid fa-table-list',
          command: () => {
            this.showPanel(ViewEnum.ProductTags);
          },
        },
        {
          label: 'Issuance Limits',
          icon: 'fa-regular fa-calculator',
          command: () => {
            this.showPanel(ViewEnum.Issuances);
          },
        },
        {
          label: 'Allowances',
          icon: 'fa-duotone fa-solid fa-dollar-sign',
          command: () => {
            this.showPanel(ViewEnum.Allowances);
          },
        },
        {
          label: 'Rapid Orders',
          icon: 'fa-regular fa-registered',
          command: () => {
            this.showPanel(ViewEnum.RapidTemplates);
          },
        },
        {
          label: 'Budgets',
          icon: '<i class="fa-duotone fa-solid fa-calculator',
          command: () => {
            this.showPanel(ViewEnum.Budgets);
          },
        },
        {
          label: 'Billing',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Billing);
          },
        },
        // Everything below acts on the program as a whole rather than configuring part of it,
        // which is why it sits apart from the setup items above — and why the whole block is
        // hidden on an Inactive program.
        //
        // An Inactive program will never route another order. Changing its end date reads as
        // reviving it without actually doing so, and moving users between its groups changes
        // nothing that can still happen. Offering either invites an edit that appears to work and
        // achieves nothing.
        //
        // The separator goes with them. A rule with nothing under it is a stray line.
        ...(this.program.activeStatusId !== ActiveStatusEnum.Inactive
          ? [
              { separator: true },
              // Only for programs that HAVE an end date to move. A Variable Start and End Date
              // program derives its end from each user's join date, so there is no single date.
              ...(this.program.hasExplicitEndDate()
                ? [
                    {
                      label: 'Change End Date',
                      icon: 'fa-regular fa-calendar-pen',
                      command: () => {
                        this.onChangeEndDate();
                      },
                    },
                  ]
                : []),
              {
                label: 'Program User Group Transfer',
                icon: 'fa-duotone fa-solid fa-people-arrows',
                command: () => {
                  this.showPanel(ViewEnum.PugTransfer);
                },
              },
              {
                label: 'Shipping Group Transfer',
                icon: 'fa-duotone fa-solid fa-truck-arrow-right',
                command: () => {
                  this.showPanel(ViewEnum.ShippingTransfer);
                },
              },
            ]
          : []),
      ];
    }

    if (this.program.programTypeId == ProgramTypeEnum.Inventory) {
      this.items = [
        {
          label: 'Information',
          icon: 'pi pi-fw pi-cog',
          command: () => {
            this.showPanel(ViewEnum.Info);
          },
        },
        {
          label: 'Supplier Settings',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Suppliers);
          },
        },
        {
          label: 'Program User Groups',
          icon: 'pi pi-fw pi-user',
          command: () => {
            this.showPanel(ViewEnum.UserGroups);
          },
        },
        {
          label: 'Products and Tags',
          icon: 'fa-duotone fa-regular fa-tags',
          command: () => {
            this.showPanel(ViewEnum.ProductTypeConfigs);
          },
        },
        {
          label: 'Kits',
          icon: 'fa-duotone fa-regular fa-suitcase',
          command: () => {
            this.showPanel(ViewEnum.Kits);
          },
        },
        {
          label: 'Menu Categories',
          icon: 'fa-duotone fa-solid fa-table-list',
          command: () => {
            this.showPanel(ViewEnum.ProductTags);
          },
        },
        {
          label: 'Issuance Limits',
          icon: 'fa-regular fa-calculator',
          command: () => {
            this.showPanel(ViewEnum.Issuances);
          },
        },
        {
          label: 'Allowances',
          icon: 'fa-duotone fa-solid fa-dollar-sign',
          command: () => {
            this.showPanel(ViewEnum.Allowances);
          },
        },
        {
          label: 'Rapid Orders',
          icon: 'fa-regular fa-registered',
          command: () => {
            this.showPanel(ViewEnum.RapidTemplates);
          },
        },
        {
          label: 'Budgets',
          icon: '<i class="fa-duotone fa-solid fa-calculator',
          command: () => {
            this.showPanel(ViewEnum.Budgets);
          },
        },
        {
          label: 'Billing',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Billing);
          },
        },
      ];
    }

    if (this.program.programTypeId == ProgramTypeEnum.Inventory) {
      this.items = [
        {
          label: 'Information',
          icon: 'pi pi-fw pi-cog',
          command: () => {
            this.showPanel(ViewEnum.Info);
          },
        },
        {
          label: 'Supplier Settings',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Suppliers);
          },
        },
        {
          label: 'Program User Groups',
          icon: 'pi pi-fw pi-user',
          command: () => {
            this.showPanel(ViewEnum.UserGroups);
          },
        },
        {
          label: 'Products and Tags',
          icon: 'fa-duotone fa-regular fa-tags',
          command: () => {
            this.showPanel(ViewEnum.ProductTypeConfigs);
          },
        },
        {
          label: 'Menu Categories',
          icon: 'fa-duotone fa-solid fa-table-list',
          command: () => {
            this.showPanel(ViewEnum.ProductTags);
          },
        },
        // {
        //   label: 'Allowances',
        //   icon: 'fa-duotone fa-solid fa-dollar-sign',
        //   command: () => {
        //     this.showPanel(ViewEnum.Allowances);
        //   },
        // },
        {
          label: 'Budgets',
          icon: '<i class="fa-duotone fa-solid fa-calculator',
          command: () => {
            this.showPanel(ViewEnum.Budgets);
          },
        },
        {
          label: 'Billing',
          icon: 'fa-regular fa-file-invoice',
          command: () => {
            this.showPanel(ViewEnum.Billing);
          },
        },
      ];
    }

    // Claude guidelines are GlobalAdmin data: only a GlobalAdmin may read or save them.
    if (this.authUser?.isGlobalAdmin) {
      const at = this.items.findIndex((x) => x.label === 'Billing');
      this.items.splice(at < 0 ? this.items.length : at + 1, 0, {
        label: 'Claude Guidelines',
        icon: 'fa-duotone fa-solid fa-book-open-cover',
        command: () => {
          this.showPanel(ViewEnum.Guidelines);
        },
      });
    }

    // Throwaway: one-shot Program Product Reconcile feature. Global Admin only,
    // AND only when the URL carries the expected ?reconcile=<key> param —
    // otherwise the menu item never renders. The same key is sent to the server
    // as the X-Reconcile-Key header (set in program-reconcile.sub.component.ts).
    // Remove this block when the cleanup work is finished.
    if (this.authUser?.hasOrigRole('GlobalAdministrator') && this.route.snapshot.queryParamMap.get('reconcile') === 'Back2Zero100!') {
      this.items.push({
        label: 'Reconcile with Uniformax',
        icon: 'fa-regular fa-arrows-rotate',
        command: () => {
          this.showPanel(ViewEnum.Reconcile);
        },
      });
    }

    this.programMenuService.setMenuItems(this.program.id, this.program.name, this.items);
  }

  async showPanel(selectedViewEnum: ViewEnum) {
    if (this.program.approvalTreeId == Guid.EMPTY) {
      this.toastr.warning('Proximity Organization tree must be set before navigating to any other area of the program.');
      return;
    }
    this.activeView = selectedViewEnum;
  }

  isView(viewEnum: ViewEnum) {
    return this.activeView == viewEnum;
  }

  getViewTitle(): string {
    switch (this.activeView) {
      case ViewEnum.Info:
        return 'Program Information';
      case ViewEnum.Suppliers:
        return 'Program Supplier Settings';
      case ViewEnum.UserGroups:
        return 'Program User Groups';
      case ViewEnum.ProductTypeConfigs:
        return 'Program Products and Tags';
      case ViewEnum.Kits:
        return 'Program Kits';
      case ViewEnum.ProductTags:
        return 'Program Menu Categories';
      case ViewEnum.ProductViews:
        return 'Program Compliance';
      case ViewEnum.Issuances:
        return 'Program Issuance Limits';
      case ViewEnum.Allowances:
        return 'Program Allowances';
      case ViewEnum.RapidTemplates:
        return 'Program Rapid Orders';
      case ViewEnum.Budgets:
        return 'Program Budgets';
      case ViewEnum.Billing:
        return 'Program Billing';
      case ViewEnum.Cancellation:
        return 'Program Cancellation';
      case ViewEnum.Reconcile:
        return 'Reconcile with Uniformax';
      case ViewEnum.PugTransfer:
        return 'Program User Group Transfer';
      case ViewEnum.ShippingTransfer:
        return 'Shipping Group Transfer';
      case ViewEnum.Guidelines:
        return 'Program Claude Guidelines';
      default:
        return 'Program';
    }
  }

  private createNewProgram() {
    let ptId;
    if (this.newProgramType.programType === 'inv') {
      ptId = ProgramTypeEnum.Inventory;
    } else {
      ptId = ProgramTypeEnum.Managed;
    }

    this.program = this.dbSaveService.createEntity(Program, {
      accountId: this.accountId,
      activeStatusId: ActiveStatusEnum.Hold,
      programTypeId: ptId,
      programAllowanceRemainderRuleId: ProgramAllowanceRemainderRuleEnum.RemainingAllowanceAddedToNewAllowances,
      issuanceProgramViolationRuleId: ProgramViolationRuleEnum.Warn,
      allowanceProgramViolationRuleId: ProgramViolationRuleEnum.Warn,
      approvalTreeId: UtilFns.EmptyGuid,
    });
    // this.program.entityAspect.validateEntity();
    this.program.entityAspect.setUnchanged();
    return this.program;
  }

  /** Set by onCopyProgram when the outgoing cycle was still live, so its tree's group settings
   *  could not be cleared. Read by the cycle dialog, which says so rather than letting last
   *  cycle's purchase orders sit there looking like this cycle's. Null when they were cleared. */
  cycleDeferredTreeName: string | null = null;

  async onCopyProgram(newData: IProgramNewCycleData) {
    //this.toastr.info('Under construction...');
    //return;

    if (this.dbSaveService.hasChanges()) {
      this.toastr.warning('You must save/undo before copying this Proximity Program. Save/undo first.', 'Unable to Proceed');
      return;
    }

    // if (this.program.activeStatusId == ActiveStatusEnum.Active || this.program.activeStatusId == ActiveStatusEnum.Hold) {
    //   this.toastr.warning('Programs must be set to Status Phaseout or Inactive to copy.', 'Unable to Proceed');
    //   return;
    // }

    // A collection episode has to finish inside the cycle that opened it. Cycling turns Collect,
    // Organize and Place off on the OUTGOING program (below), and the release action only exists
    // while a program is collecting — so cycling now would strand the batch with no way to place it.
    // Only worth asking of a program that is actually collecting, so an ordinary cycle costs no round trip.
    const collected = this.program.collectAndPlace ? await this.dbQueryService.getCollectedOrderCountForProgram(this.program.id) : 0;
    if (collected > 0) {
      await this.dialogService.okDialog(
        'Cannot Start a New Cycle Yet',
        `This Proximity Program has ${collected} collected order${collected === 1 ? '' : 's'} waiting to be released. ` +
          `Starting a new cycle turns Collect, Organize and Place off on this Program, which would leave those orders ` +
          `with no way to reach a supplier.<br/><br/>Release the batch first, then start the new cycle.`,
      );
      return;
    }

    // Insure that everything we need to copy is here.
    await this.dbQueryService.getProgramByIdFull(this.program.id);
    // getProgramByIdFull does not reach the tree's group rows, and the cycle has to clear them.
    await this.dbQueryService.getProgramWithApprovalTree(this.program.id);

    // Whether the tree's group settings can be cleared right now. Building the new cycle is always
    // allowed — name, dates, allowances, issuances, views, suppliers and product configs are all
    // program-scoped and touch nothing on the tree. Only the wipe is dangerous, and only while the
    // outgoing Program can still place an order through those same rows: clear them then and its
    // next order resolves to no purchase order at all, silently, at placement.
    //
    // isAcceptingNewOrders, not activeStatusId === Active: in production plenty of Active programs
    // are already expired, and an expired one routes nothing.
    const outgoingStillLive = this.program.isAcceptingNewOrders();

    const uow = this.dbSaveService.uow;
    // const newProgram = EntityFns.copyEntity(uow, Program, this.program, ['id', 'name', 'startDate', 'accountPurchaseOrderId'], (e) => {
    //   e.name = this.program.name + ' - Copy';
    //   e.activeStatusId = ActiveStatusEnum.Hold;
    // });

    const newProgram = EntityFns.copyEntity(
      uow,
      Program,
      this.program,
      ['id', 'name', 'startDate', 'endDate', 'accountPurchaseOrderId'],
      (e) => {
        e.name = newData.newProgramName;
        e.startDate = newData.newStartDate;
        e.endDate = newData.newEndDate;
        e.activeStatusId = ActiveStatusEnum.Hold;
        e.programPurchaseOrderTypeId = ProgramPurchaseOrderTypeEnum.None;
        e.isPurchaseOrderRequired = false;
        e.programPcardTypeId = ProgramPcardTypeEnum.None;
        e.isPcardRequired = false;
        // Nothing about a collection episode survives a cycle. The purchase orders differ, the
        // dates differ, the roster differs. Carrying CollectionReleasedTs forward would be the
        // worst of it: the new cycle would show "collect orders for review" ticked and quietly
        // collect nothing, because the batch would already read as released.
        e.collectAndPlace = false;
        e.collectionScopeId = CollectionScopeEnum.ProgramWide;
        e.earliestPlacementDate = undefined;
        e.collectionReleasedTs = undefined;
      },
    );

    // The outgoing cycle stops collecting too — its episode ended when this cycle was declared.
    // Guarded above: this is only reachable once the batch has been released.
    this.program.collectAndPlace = false;
    this.program.collectionScopeId = CollectionScopeEnum.ProgramWide;
    this.program.earliestPlacementDate = undefined;
    this.program.collectionReleasedTs = undefined;

    // approvalTreeId is deliberately NOT in the exclusion list above: the SAME Organization Tree
    // moves to the new cycle rather than being copied, so a designated tree follows its program
    // forward and no second copy accumulates on the account.
    //
    // Its per-group settings do not follow. Purchase orders, p-cards and ordering time frames all
    // have to be entered anew for the new cycle, so clear them here rather than leave last cycle's
    // values looking authoritative.
    //
    // Only on a designated tree. A shared tree's group rows are not this program's to clear —
    // other current programs read the same rows. (Those are the legacy trees this whole feature
    // exists to unwind; guard 4 forces designation at activation when the program needs it.)
    //
    // Deferred, not skipped, when the outgoing Program is still live: those rows are still routing
    // its orders. The handover is forced anyway — guard 4(b) refuses to activate this cycle while
    // another Program is still accepting orders on the same designated tree — so the administrator
    // retires the outgoing cycle and replaces the settings before this one can go anywhere. What
    // the wipe buys is that last cycle's values do not sit there looking like this cycle's, so
    // when it cannot run, the cycle dialog says so instead.
    this.cycleDeferredTreeName = null;
    const tree = this.program.approvalTree;
    if (tree?.isProgramSpecific) {
      if (outgoingStillLive) {
        this.cycleDeferredTreeName = tree.name;
      } else {
        tree.approvalTreeUserGroups.forEach((atug) => {
          atug.accountPurchaseOrderId = undefined;
          atug.accountProcurementCardId = undefined;
          atug.orderTimeFrameStartDays = undefined;
          atug.orderTimeFrameDurationDays = undefined;
          atug.orderTimeFrameShipByFromStartDays = undefined;
          atug.collectionReleasedTs = undefined;
        });
      }
    }

    this.program.programAccountIssuanceMaps.forEach((tag) => {});

    this.program.programProductCategoryTags.forEach((tag) => {
      const newTag = EntityFns.copyEntity(uow, ProgramProductCategoryTag, tag, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
      tag.programProductCategoryTagMaps.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramProductCategoryTagMap, x, ['programProductCategoryTagId'], (e) => {
          e.programProductCategoryTagId = newTag.id;
        });
      });
    });

    this.program.programAllowances.forEach((pa) => {
      const newPa = EntityFns.copyEntity(uow, ProgramAllowance, pa, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
      pa.programAllowanceAddonExceptions.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramAllowanceAddonException, x, ['programAllowanceId'], (e) => {
          e.programAllowanceId = newPa.id;
        });
      });
      pa.programAllowanceFeatureExceptions.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramAllowanceFeatureException, x, ['programAllowanceId'], (e) => {
          e.programAllowanceId = newPa.id;
        });
      });
      pa.programAllowanceUserGroupMaps.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramAllowanceUserGroupMap, x, ['programAllowanceId'], (e) => {
          e.programAllowanceId = newPa.id;
        });
      });
    });

    this.program.programIssuances.forEach((pi) => {
      const newPi = EntityFns.copyEntity(uow, ProgramIssuance, pi, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
      pi.programIssuanceUserGroupMaps.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramIssuanceUserGroupMap, x, ['programIssuanceId'], (e) => {
          e.programIssuanceId = newPi.id;
        });
      });
    });

    this.program.programViews.forEach((pv) => {
      const newPv = EntityFns.copyEntity(uow, ProgramView, pv, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
      pv.programViewProductTypeConfigs.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramViewProductTypeConfig, x, ['programViewId'], (e) => {
          e.programViewId = newPv.id;
        });
      });
    });

    this.program.programSuppliers.forEach((ps) => {
      EntityFns.copyEntity(uow, ProgramSupplier, ps, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
    });

    this.program.programProductTypeConfigs.forEach((ptConfig) => {
      EntityFns.copyEntity(uow, ProgramProductTypeConfig, ptConfig, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
    });

    this.program.programBudgets.forEach((budget) => {});

    this.program.programRapidTemplates.forEach((rapidTemplate) => {
      const newRapidTemplate = EntityFns.copyEntity(uow, ProgramRapidTemplate, rapidTemplate, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
      rapidTemplate.programRapidTemplateProductTypeConfigs.forEach((x) => {
        EntityFns.copyEntity(uow, ProgramRapidTemplateProductTypeConfig, x, ['programRapidTemplateId'], (e) => {
          e.programRapidTemplateId = newRapidTemplate.id;
        });
      });
    });

    this.program.programBillingConfigs.forEach((pbc) => {
      EntityFns.copyEntity(uow, ProgramBillingConfig, pbc, ['id', 'programId'], (e) => {
        e.programId = newProgram.id;
      });
    });

    // Claude rules written for this program follow it to the new cycle: the new cycle joins each
    // rule's program list, so one later edit covers both. GlobalAdmin only - the rules are
    // GlobalAdmin data, and an Account Admin's save may not touch them.
    if (this.authUser?.isGlobalAdmin) {
      const rules = await this.dbQueryService.getGuidelinesForProgram(this.program.id);
      rules
        .filter((g) => !g.isAllPrograms)
        .forEach((g) => this.dbSaveService.createEntity(GuidelineProgram, { guidelineId: g.id, programId: newProgram.id }));
    }

    newProgram.entityAspect.setUnchanged();
    this.isBeingAdded = true;
    this.program = newProgram;

    // important to insure they all reflect the new ids
    this.refreshAllSubs();
    return this.program;
  }

  /**
   * Move the program's end date on its own, without the Hold / edit / save / re-Activate
   * ritual. Deliberately available while the program is Active - an Active program is exactly
   * the one whose end date is worth extending, and forcing Hold first is the step admins
   * forget, which leaves the program silently blocking orders.
   */
  async onChangeEndDate() {
    if (this.dbSaveService.hasChanges()) {
      await this.dialogService.okDialog(
        'Cannot Change End Date',
        'Please save or cancel your other changes first.',
      );
      return;
    }

    const newEndDate = await ProgramChangeEndDateDialogComponent.openDialog(
      this.pngDialogService,
      { program: this.program },
      { header: 'Change End Date: ' + this.program.name },
    );
    if (!newEndDate) return;

    this.program.endDate = newEndDate;
    await this.dbSaveService.saveSelectedChanges([this.program]);
    this.toastr.success(
      'End date changed to ' + DateFns.fmtDateShort(newEndDate),
      this.program.name,
    );
  }

  async onProgramDelete(program: Program) {
    this.toastr.warning('Not yet implemented', 'In Progress');
    return;

    // TODO: need to write this.
    // may be better to do this on the server.

    program.programProductTypeConfigs.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    program.programIssuances.slice().forEach((x) => {
      x.programIssuanceUserGroupMaps.slice().forEach((y) => EntityFns.deleteOrDetach(y.entityAspect));
      EntityFns.deleteOrDetach(x.entityAspect);
    });

    EntityFns.deleteOrDetach(program.entityAspect);

    this.toastr.success('Program deleted.');
    this.onCancel();
  }

  onConsolidateMe() {
    const productConfigsSub = this.subComponents.find((c) => c instanceof ProgramProductTypeConfigsSubComponent) as
      | ProgramProductTypeConfigsSubComponent
      | undefined;

    if (productConfigsSub) {
      productConfigsSub.consolidateMe();
    } else {
      this.toastr.error('Unable to find Products and Tags component.');
    }
  }

  // ---------- Save and Undo --------------------------------


  // Program saves routinely produce dozens of repeated errors (the same rule against
  // every PUG / PricedProductType / etc.). Opt in to the dialog's collapse-by-rule
  // grouping so the wall of identical messages becomes a single expandable header.
  // Other forms keep the default flat list — better for small validation results.
  override async handleBreezeValidationErrors(errEntities: Entity[]) {
    await this.dialogService.validationDialog(
      this, errEntities, this.navigateToValidationError.bind(this), true, true);
  }

  /**
   * ATUGs that actually participate in ordering. Mirrors program-info.sub's assignableAtugs() —
   * consolidated-out Inactive groups never had time frames configured and must not block activation.
   */
  private collectableAtugs() {
    return (this.program.approvalTree?.approvalTreeUserGroups ?? []).filter(
      (a) => a.activeStatusId !== ActiveStatusEnum.Inactive && a.programUserGroup?.activeStatusId !== ActiveStatusEnum.Inactive,
    );
  }

  /**
   * Collect, Organize and Place — the two configuration rules that BLOCK activation.
   *
   * Unlike the release-time gates (which warn and never block), a misconfigured collection is not
   * recoverable after the fact: orders would collect against a group that has no placement date, or
   * a program-wide batch would come due while some group's users are still mid-window. Both are
   * cheap to prevent here and expensive to discover later.
   */
  /**
   * Backstop for the two collection settings that cannot change while orders are collected.
   *
   * program-info.sub refuses both at the control, which is where an administrator gets immediate
   * feedback. This catches any other route to the same state — an undo/redo sequence, a stale form,
   * a future caller — because the consequence is orders stranded in AwaitingRelease with no way to
   * reach a supplier, and that is worth checking twice.
   *
   * Only queries when something actually changed, so the common save costs no round trip.
   */
  private async addCollectedOrderValidationErrors() {
    const original = this.program.entityAspect?.originalValues as Record<string, unknown> | undefined;
    if (!original) return;

    const turnedOff = 'collectAndPlace' in original && original['collectAndPlace'] === true && !this.program.collectAndPlace;
    const scopeChanged = 'collectionScopeId' in original;
    if (!turnedOff && !scopeChanged) return;

    const collected = await this.dbQueryService.getCollectedOrderCountForProgram(this.program.id);
    if (collected === 0) return;

    const orders = `${collected} collected order${collected === 1 ? '' : 's'}`;
    if (turnedOff) {
      this.createValidationError(
        this.program,
        'collectAndPlace',
        `Collect, Organize and Place cannot be turned off while this Program has ${orders} waiting to be released — ` +
          `they would be stranded, since a collected order only reaches a supplier through the release action. ` +
          `Release the batch first.`,
        'CapOffWithCollected',
      );
    }
    if (scopeChanged) {
      this.createValidationError(
        this.program,
        'collectionScopeId',
        `The collection scope cannot change while this Program has ${orders} waiting to be released — scope decides how ` +
          `they are grouped for release. Release the batch first.`,
        'CapScopeWithCollected',
      );
    }
  }

  /**
   * Guard 4 of Docs/Program-Specific-Org-Trees.md — the seam between the two pieces of work.
   *
   * A Program that stores anything per Program User Group needs its Organization Tree dedicated to
   * it. Nothing stopped the tree being released back to the shared pool afterwards, so a Program
   * could be activated with per-PUG purchase orders on a tree shared with other Programs — which is
   * the whole failure this work exists to prevent.
   *
   * ACTIVATION ONLY, like the other readiness rules: the tree can legitimately be shared while the
   * Program is still being built on Hold.
   */
  private async addProgramSpecificTreeValidationError() {
    if (this.activatingStatusCode == null) return;

    const tree = this.program.approvalTree;
    if (!tree) return;

    // Needs a dedicated tree but sits on a shared one.
    if (this.requiresProgramSpecificTree() && !tree.isProgramSpecific) {
      this.createValidationError(
        this.program,
        'approvalTreeId',
        `This Program stores settings per Program User Group, so its Proximity Organization Tree must be dedicated to it. ` +
          `'${tree.name}' is currently shared with other Programs.`,
        'ProgSpecTreeReq',
      );
      return;
    }

    // On a dedicated tree, it must be dedicated to THIS Program. Guard 1 checks that at the moment
    // of designation, but nothing re-checked it afterwards — and a program cycle deliberately
    // leaves two Programs pointing at one designated tree, so this is the door the collision comes
    // back through. Active-scoped: the outgoing cycle sitting on Hold is legal and expected.
    if (tree.isProgramSpecific) {
      const others = await this.dbQueryService.getOtherActiveProgramsOnTree(tree.id, this.program.id);
      if (others.length > 0) {
        const one = others.length === 1;
        this.createValidationError(
          this.program,
          'approvalTreeId',
          `'${tree.name}' is dedicated to a single Program, and ${one ? 'another Program is' : `${others.length} other Programs are`} ` +
            `already active on it: ${others.join(', ')}. Retire ${one ? 'it' : 'them'} before activating this Program, ` +
            `or give this Program its own Organization Tree.`,
          'ProgSpecTreeTaken',
        );
      }
    }
  }

  private addCollectAndPlaceValidationErrors() {
    // ACTIVATION ONLY. These are readiness checks, not data-integrity ones — a program with
    // PUG-wide collection and no ordering time frames yet is an incomplete configuration, not an
    // invalid one. Hold is the editing state and must stay freely saveable; completeness is what
    // activation is for. Same gate the program-expiry check uses.
    //
    // The collected-order guards are deliberately NOT gated this way: stranding orders in
    // AwaitingRelease is a data problem regardless of whether anyone is activating.
    if (this.activatingStatusCode == null) return;
    if (!this.program.collectAndPlace) return;

    const atugs = this.collectableAtugs();

    // A collection needs a beginning. Without it the one-order-per-collection rule has no
    // boundary and silently does nothing — the only alternative boundary, "any order on this
    // program", reaches back into previous cycles. Normally set automatically when collection is
    // switched on, so this catches an administrator having cleared it. §4b.
    if (this.program.collectionStartDate == null) {
      this.createValidationError(
        this.program,
        'collectionStartDate',
        'A Collection Start Date is required while this program collects orders. Each Account User may ' +
          'place one order from that date.',
        'CapCollectionStartReq',
      );
    }

    if (this.program.collectionScopeId === CollectionScopeEnum.ProgramUserGroupWide) {
      // Every group needs a window, because under this scope the group's placement date IS the end
      // of its window. A group with nulls would collect forever with no date to release against.
      const missing = atugs.filter((a) => a.orderTimeFrameStartDays == null || a.orderTimeFrameDurationDays == null);
      if (missing.length > 0) {
        // Terse on purpose, matching the other activation rules. Naming all the groups made this
        // unreadable at 25 of 25, and the admin has better places to see WHICH: the collection
        // scope hint on this form carries the count, and the Program User Groups tab shows the
        // time-frame columns per group.
        this.createValidationError(
          this.program,
          'collectionScopeId',
          `Every active Program User Group must have a Restricted Ordering Time Frame when the collection scope is ` +
            `Program User Group Wide. ${missing.length} of ${atugs.length} do not.`,
          'CapPugWindowReq',
        );
      }
      return;
    }

    // Program Wide — one batch for everyone, so it cannot come due before the LAST group's ordering
    // window closes, or that group's users are cut off mid-order.
    if (this.program.earliestPlacementDate == null) {
      this.createValidationError(
        this.program,
        'earliestPlacementDate',
        'Collect, Organize and Place is enabled, so an Earliest Placement Date is required. This is the date published to the organization.',
        'CapDateReq',
      );
      return;
    }

    if (this.program.startDate == null) return; // start-date requirement is validated separately

    const windowed = atugs.filter((a) => a.orderTimeFrameStartDays != null && a.orderTimeFrameDurationDays != null);
    if (windowed.length === 0) return;

    const lastWindowEndDays = Math.max(...windowed.map((a) => <number>a.orderTimeFrameStartDays + <number>a.orderTimeFrameDurationDays));
    const lastWindowEnd = DateFns.dateAdd(this.program.startDate, lastWindowEndDays, 'days');

    if (this.program.earliestPlacementDate.getTime() <= lastWindowEnd.getTime()) {
      this.createValidationError(
        this.program,
        'earliestPlacementDate',
        `The Earliest Placement Date (${DateFns.fmtDate(this.program.earliestPlacementDate)}) must fall after every program user ` +
          `group's ordering window has closed. The last one closes ${DateFns.fmtDate(lastWindowEnd)}. ` +
          `Releasing before then would cut off users who are still able to order.`,
        'CapDateTooEarly',
      );
    }
  }

  override async addCrossValidationErrors() {
    if (this.program.programTypeId != ProgramTypeEnum.Managed && this.program.programTypeId != ProgramTypeEnum.Inventory) {
      this.createValidationError(
        this.program,
        'programTypeId',
        'Only Managed Programs and Inventory Programs are supported at this time.',
        'ManagedOnly',
      );
    }

    // A new Program starts on the empty Guid (createNewProgram), which a falsy test lets through.
    if (!this.program.approvalTreeId || this.program.approvalTreeId === UtilFns.EmptyGuid) {
      this.createValidationError(
        this.program,
        'approvalTreeId',
        'The program has no Proximity Organization. Select a Proximity Organization, then save the program.',
        'OrgReq',
      );
    }

    // TODO: check for dup budgets.

    const dupPrograms = await this.dbQueryService.getProgramsWithSameName(this.program.id, this.program.name, this.program.accountId);
    if (dupPrograms.length > 0 && dupPrograms.some((prog) => prog.activeStatusId == ActiveStatusEnum.Active)) {
      this.createValidationError(this.program, 'name', 'This account already has an active program with this name.', 'DupName');
    }
    // OLD CODE
    // const ok = await this.dbQueryService.checkIfIsUnique(this.program, 'id', 'accountId', 'name');
    // if (!ok) {
    //   this.createValidationError(this.program, 'name', 'This account already has a program with this name.', 'DupName');
    // }

    EntityFns.checkForDupErrors(
      this.program.programViews,
      (pi) => pi.name?.toLowerCase(),
      (e, dupName) => this.createValidationError(e, 'name', `Duplicate Program View Name: '${dupName}'`, 'DupViewName'),
    );

    EntityFns.checkForDupErrors(
      this.program.programIssuances,
      (pi) => pi.name?.toLowerCase(),
      (e, dupName) => this.createValidationError(e, 'name', `Duplicate Program Issuance Name: '${dupName}'`, 'DupIssuanceName'),
    );

    EntityFns.checkForDupErrors(
      this.program.programAllowances,
      (pa) => pa.name?.toLowerCase(),
      (e, dupName) => this.createValidationError(e, 'name', `Duplicate Program Allowance Name: '${dupName}'`, 'DupAllowanceName'),
    );

    EntityFns.checkForDupErrors(
      this.program.programBudgets,
      (x) => x.budgetProductTypeTag?.name || '- Any -',
      (e, dupName) => this.createValidationError(e, 'name', `Duplicate Budget Product Tag Name: '${dupName}'`, 'DupBudgetTagName'),
    );

    EntityFns.checkForDupErrors(
      this.program.programRapidTemplates,
      (x) => x.name?.toLowerCase(),
      (e, dupName) => this.createValidationError(e, 'name', `Duplicate Rapid Order Name: '${dupName}'`, 'DupRapidOrderName'),
    );

    await this.addProgramSpecificTreeValidationError();
    this.addCollectAndPlaceValidationErrors();
    await this.addCollectedOrderValidationErrors();

    // Check if program has expired
    if (
      this.program.programTypeId === ProgramTypeEnum.Managed &&
      this.program.programStartDateTypeId == ProgramStartDateTypeEnum.FixedStartAndEndDate
    ) {
      if (this.program.startDate == null) {
        this.createValidationError(
          this.program,
          'startDate',
          `The Proximity Program start date is required when the Proximity Program start date type is 'Fixed Start and End Date'.`,
          'StartDateReq',
        );
      }
    }
    if (this.program.programTypeId === ProgramTypeEnum.Managed && this.program.hasExplicitEndDate()) {
      const expirationDate = this.program.endDate;
      if (expirationDate == null) {
        this.createValidationError(
          this.program,
          'endDate',
          'The Proximity Program end date is required for this Proximity Program start date type.',
          'EndDateReq',
        );
      }
      if (expirationDate && new Date().getTime() > expirationDate.getTime() && this.activatingStatusCode != null) {
        this.createValidationError(
          this.program,
          'term',
          'This Proximity Program has expired and may not be set to Active.',
          'ExpCannotActivate',
        );
      }
    }

    if (this.program.programStartDateTypeId == ProgramStartDateTypeEnum.VariableStartAndEndDate) {
      const durationDays = this.program.durationDays;

      if (durationDays == null || durationDays <= 0) {
        this.createValidationError(
          this.program,
          null,
          'Variable Start and End Date programs require a Duration in days.',
          'DurationDaysReq',
        );
      }
    }

    if (this.program.isPurchaseOrderRequired && this.program.programPurchaseOrderTypeId == ProgramPurchaseOrderTypeEnum.None) {
      this.createValidationError(
        this.program,
        null,
        "A Purchase Order Type is required when 'A Purchase Order is required for every order.'",
        'OrderTypeReq',
      );
    }

    if (this.program.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgram && !this.program.accountPurchaseOrderId) {
      this.createValidationError(this.program, null, 'A Program purchase order must be selected.', 'OrderMustBeSelected');
    }

    if (this.program.isPcardRequired && this.program.programPcardTypeId == ProgramPcardTypeEnum.None) {
      this.createValidationError(
        this.program,
        null,
        "A P-card Type is required when 'A procurement card is required for every order'.",
        'PcardTypeReq',
      );
    }

    if (this.program.programPcardTypeId === ProgramPcardTypeEnum.ByProgram && !this.program.accountProcurementCardId) {
      this.createValidationError(this.program, null, 'A Program p-card must be selected', 'PcardMustBeSelected');
    }

    for (const ps of this.program.programSuppliers) {
      if (ps.allowInventory && !ps.inventoryProgramId) {
        this.createValidationError(
          ps,
          null,
          `An Inventory Program must be selected for supplier "${ps.supplier?.name}" when "Use Managed Inventory" is checked.`,
          'InventoryProgramRequired',
        );
      }
    }

    if (this.program.programBillingConfigs.length == 0 && !this.isBeingAdded) {
      this.createValidationError(this.program, null, 'At least one Program Billing Config is required.', 'ProgramBillingConfigReq');
    }

    // Duplicate detection: per (UserGroup, Supplier) tuple, at most one PBC row. A "null Supplier"
    // (default row that applies to any supplier without a more-specific row) counts as its own slot.
    // Previously this was per-UserGroup with a separate "no two billing-address rows" check; now that
    // supplier-specific billing-address rows (shape B) are allowed, the rule is unified.
    const tupleMap = UtilFns.getDuplicatesOnProp(
      this.program.programBillingConfigs,
      (x) => `${x.programUserGroup?.name ?? ''}|${x.supplierId ?? ''}`,
    );
    Object.keys(tupleMap).forEach((tupleKey) => {
      const pbcs = tupleMap[tupleKey];
      const [ugName, supplierId] = tupleKey.split('|');
      const groupLabel = ugName === '' ? '- Default -' : ugName;
      const supplierLabel = supplierId === '' ? '- Any -' : (pbcs[0].supplier?.name ?? supplierId);
      this.createValidationError(
        this.program,
        null,
        `Duplicate Program Billing Configs with the same Program User Group (${groupLabel}) and Supplier (${supplierLabel}).`,
        'ProgramBillingConfigDupTuple',
      );
    });

    this.program.programBillingConfigs.forEach((pbc) => {
      // Each row must take one of three shapes (see ProgramBillingConfig doc-comment):
      //   A: billingAccountAddressId set, supplierId null,    manifestId null
      //   B: billingAccountAddressId set, supplierId set,     manifestId null
      //   C: billingAccountAddressId null, supplierId set,    manifestId set
      const hasAddr = pbc.billingAccountAddressId != null;
      const hasManifest = pbc.manifestId != null;
      const hasSupplier = pbc.supplierId != null;

      if (!hasAddr && !hasManifest) {
        this.createValidationError(
          this.program,
          null,
          'Every Program Billing Config must have either a billing address or a manifest.',
          'ProgramBillingConfigDefaultReq2',
        );
      }
      if (hasAddr && hasManifest) {
        this.createValidationError(
          this.program,
          null,
          'A Program Billing Config cannot have both a billing address and a manifest.',
          'ProgramBillingConfigDefaultReq3',
        );
      }
      if (hasManifest && !hasSupplier) {
        // Defensive: a manifest implies a supplier (set automatically when the manifest is chosen).
        this.createValidationError(
          this.program,
          null,
          'A manifest-based Program Billing Config must have a Supplier.',
          'ProgramBillingConfigManifestNeedsSupplier',
        );
      }
      if (hasManifest && pbc.manifest.activeStatusId != ActiveStatusEnum.Active) {
        this.createValidationError(
          this.program,
          null,
          `Program Billing Config manifest "${pbc.manifest.name}" is not Active and must be Active in order to be used by a Program Billing Config.`,
          'ProgramBillingConfigManifestActiveReq',
        );
      }
    });

    // Validations below this point are only run when activating the program.

    if (this.activatingStatusCode != null) {
      const expirationDate = this.program.endDate;
      if (this.program.hasExplicitEndDate() && expirationDate && new Date().getTime() > expirationDate.getTime()) {
        this.createValidationError(
          this.program,
          null,
          'An expired Proximity Program may not be activated or phased out.',
          'ExpCannotPhaseOut',
        );
      }

      // Detect any user/programUserGroup duplicates within the program.
      const dupPugms = await this.dbQueryService.getProgramUserGroupMapsWithDupUsers(this.program.id);
      if (dupPugms.length > 0) {
        const msg = dupPugms
          .map((x) => x.accountUser.proximityUser.fullName + ' on Program User Group: ' + x.programUserGroup.name)
          .join(`</br>`);
        this.createValidationError(
          this.program,
          null,
          `The following users exist on multiple Program User Groups used by this Program.
          All but one of these for each user will need to be excluded to remove the duplicates</br>` + msg,
          'UserExistsOnMultiGroups',
        );
      }

      // Billing-config consistency: for every (UserGroup × Supplier) tuple this program could
      // produce at JO submission, check that the resolved PBC + SupplierAccount delivery method
      // are consistent. See BillingConfigResolver — single source of truth shared with the C#
      // resolver used by JoHeaderDTO.Convert.
      const userGroups = this.program.approvalTree?.approvalTreeUserGroups.map((atug) => atug.programUserGroup) ?? [];
      const supplierAccountsBySupplierId = new Map<string, SupplierAccount>();
      this.account.supplierAccounts.forEach((sa) => supplierAccountsBySupplierId.set(sa.supplierId, sa));
      const tupleResolutions = BillingConfigResolver.resolveAll(
        this.program.programBillingConfigs,
        this.program.name,
        this.program.programSuppliers,
        userGroups,
        supplierAccountsBySupplierId,
      );
      tupleResolutions
        .filter((tr) => tr.inconsistencyReason != null)
        .forEach((tr) => {
          this.createValidationError(
            this.program,
            null,
            tr.inconsistencyReason!,
            `BillingConfigInconsistent:${tr.userGroupId ?? 'default'}:${tr.supplierId}`,
          );
        });

      // Show-Inactive semantics: skip Inactive PUGs; still enforce for Hold + Phaseout.
      // See show-inactive-checkbox-semantics memory.
      const activeAtugs = this.program.approvalTree.approvalTreeUserGroups.filter(
        (p) => p.programUserGroup?.activeStatusId !== ActiveStatusEnum.Inactive,
      );

      if (
        this.program.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup &&
        this.program.isPurchaseOrderRequired
      ) {
        activeAtugs.some((p) => {
          if (!p.accountPurchaseOrderId) {
            this.createValidationError(
              this.program,
              null,
              'Every active Program User Group must have an assigned purchase order.',
              'OrderReq',
            );
            return true;
          } else {
            return false;
          }
        });
      }

      if (this.program.programPcardTypeId === ProgramPcardTypeEnum.ByProgramUserGroup && this.program.isPcardRequired) {
        activeAtugs.some((p) => {
          if (!p.accountProcurementCardId) {
            this.createValidationError(
              this.program,
              null,
              'Every active Program User Group must have an assigned procurement card (p-card).',
              'PcardReq',
            );
            return true;
          } else {
            return false;
          }
        });
      }

      // edit end

      /*       let hasProgramUserGroups = false;
      this.account?.approvalTrees.forEach((p) => {
        if (this.approvalTreeHasProgramUserGroups(p)) {
          hasProgramUserGroups = true;
        }
      })

      if (!hasProgramUserGroups) {
        this.createValidationError(this.program, null, 'At least one Program User Group must exist within the Proximity Organization.')
      } */

      const activePugs = this.program
        .getProgramUserGroups()
        .filter((pug) => pug.activeStatusId !== ActiveStatusEnum.Inactive);
      if (activePugs.length === 0) {
        this.createValidationError(
          this.program,
          'programUserGroups',
          'This program has no active Program User Groups. Open the Program User Groups sub-page and add or reactivate at least one before activating the program.',
          'TreeUserGroupReq',
        );
      }

      // if (this.program.billingAccountAddressId == null) {
      //   this.createValidationError(this.program, 'billingAccountAddress', 'A Billing Address is required.', 'BillingAddrReq');
      // }

      if (this.program.programProductTypeConfigs.length === 0) {
        this.createValidationError(
          this.program,
          null,
          'At least one product must be added to the program before it can be activated.',
          'ProductsRequired',
        );
      }

      if (this.program.programProductTypeConfigs.length > 0 && this.program.programSuppliers.length === 0) {
        this.createValidationError(
          this.program,
          null,
          'Supplier settings are missing. Save the program to auto-create them, then try activating again.',
          'ProgramSuppliersRequired',
        );
      }

      // An Inventory Program is useless unless at least one Managed Distribution program
      // in this account is set to draw from it (allowInventory=true AND inventoryProgramId=this).
      // Admins wire that up via the grid on this program's Information page.
      if (this.program.programTypeId === ProgramTypeEnum.Inventory) {
        const mdSuppliersUsingThis = await this.dbQueryService.getManagedProgramSuppliersForAccount(this.program.accountId);
        const linkedCount = mdSuppliersUsingThis.filter(
          (ps) => ps.allowInventory && ps.inventoryProgramId === this.program.id,
        ).length;
        if (linkedCount === 0) {
          this.createValidationError(
            this.program,
            null,
            'At least one Managed Distribution program must be set to draw from this Inventory Program before it can be activated. Use the "Managed Distribution Programs" grid on the Information page to Apply this Inventory Program to one or more Managed Distribution programs.',
            'InventoryNeedsMdLinkage',
          );
        }
      }

      // Ensure kit products exist in the program's Products and Tags
      await this.dbQueryService.getProgramKitsByProgramId(this.program.id);
      const allowedPtcIdSet = new Set(this.program.programProductTypeConfigs.map((pptc) => pptc.productTypeConfigId));
      for (const pk of this.program.programKits) {
        for (const kd of pk.kit.kitDetails) {
          if (!allowedPtcIdSet.has(kd.productTypeConfigProduct.productTypeConfigId)) {
            this.createValidationError(
              this.program,
              null,
              `Kit "${pk.kit.name}" contains "${kd.productTypeConfigProduct.product.productType.name}" (config: ${kd.productTypeConfigProduct.productTypeConfig.name}) which is not in the program's Products and Tags.`,
              'KitProductNotInProgram',
            );
            break;
          }
        }
      }

      // insure that program and account issuances have been loaded.
      // JM (2/18/26) Inverntory programs don't require allowances or issuances
      if (this.program.programTypeId !== ProgramTypeEnum.Inventory) {
        await this.dbQueryService.getProgramIssuances(this.program.id);
        await this.dbQueryService.getAccountIssuances(this.program.accountId);

        this.program.programAllowances.forEach((pa) => {
          if (pa.programAllowanceUserGroupMaps.length == 0) {
            // Note: this is an unusual case - in order for cell validation errors to appear
            // they must be on a modified or added entity.  In this case there is no programAllowanceUserGroup left
            // to create a validation error on - so we need to do it to the parent programAllowance but it
            // may be unmodified - so in this one case we force a modified state on an object that was not actually changed

            // if (pa.entityAspect.entityState.isUnchanged()) {
            //   pa.entityAspect.setModified();
            // }
            this.createValidationError(
              pa,
              null,
              `
            At least one Program User Group must be selected for each allowance.`,
              'AllowanceUserGroupReq',
            );
          }

          if (pa.isFreightChargedToAllowance) {
            if (!pa.estimatedFreightChargeRate || pa.estimatedFreightChargeRate == 0.0) {
              this.createValidationError(
                pa,
                null,
                `
            Estimated freight charge is required when freight is charged to allowance.`,
                'FreightChrgeReq',
              );
            }
          }
        });

        this.program.programIssuances.forEach((iss) => {
          if (iss.programIssuanceUserGroupMaps.length == 0) {
            this.createValidationError(
              iss,
              null,
              `
            At least one user group must be selected for each issuance.`,
              'IssuanceUserGroupReq',
            );
          }
        });

        this.program.programAccountIssuanceMaps
          .filter((x) => x.programId == this.program.id)
          .forEach((paim) => {
            const iss = paim.accountIssuance;
            if (iss.accountIssuanceUserGroupMaps.length == 0) {
              this.createValidationError(
                iss,
                null,
                `At least one user group must be selected for each included account issuance.`,
                'InclIssuanceUserGroupReq',
              );
            }
          });

        if (this.program.programAccountIssuanceMaps.length > 0 && this.program.programIssuances.length > 0) {
          this.program.programIssuances.forEach((pi) => {
            if (
              this.program.programAccountIssuanceMaps.find((x) => x.accountIssuance.programProductTypeTagId === pi.programProductTypeTagId)
            ) {
              this.createValidationError(
                this.program,
                null,
                'The product type ' + pi.programProductTypeTag.name + ' may not be shared between a program and an account issuance limit.',
                'ProdTypeMustNotBeShared',
              );
            }
          });
        }

        this.program.programAllowances.forEach((pa) => {
          if (pa.allowanceAmt < 0) {
            this.createValidationError(
              this.program,
              null,
              "Allowance amount for '" + pa.name + "' must be greater than or equal to $0.00.",
              'AllowanceAmtGreaterThanZero',
            );
          }

          if (pa.allowanceAmt == 0.0) {
            if (!pa.allowOverAllowancePurchases || !pa.isFreightChargedToAllowance || pa.programAllowanceFeatureExceptions.length > 0) {
              this.createValidationError(
                this.program,
                null,
                pa.name +
                  ` has no allowance amount and therefore has the following requirements: (1) over-purchase must be enabled (2) freight must be charged to the allowance and (3) may have no excluded feature charges.`,
                'NoAllowanceAmt',
              );
            }
          }
        });

        const pugsWoAllowances = this.getProgramUserGroupsWithoutAllowances();

        // First-pass classification: separate PUGs that are fundamentally unconfigured (no allowance
        // AND no issuance — almost always "admin forgot to set anything up") from PUGs that are
        // intentionally issuance-only. Reporting them together produces cause-and-message mismatches:
        // e.g., the product-tag check below assumes "you chose issuance-only" but fires whenever any
        // PUG lacks an allowance, including the forgot-to-configure case. The clear "needs allowance
        // or issuance" message is what the admin actually needs to see; everything else is noise
        // until that's fixed.
        const unconfiguredPugs = pugsWoAllowances.filter((pug) => {
          if (this.program.programTypeId !== ProgramTypeEnum.Managed) return false;
          return this.getProgramIssuancesForPug(pug).length === 0 && this.getAccountIssuancesForPug(pug).length === 0;
        });
        const issuanceOnlyPugs = pugsWoAllowances.filter((pug) => !unconfiguredPugs.includes(pug));

        // Surface the unconfigured PUGs with the clearest possible message. Downstream checks are
        // skipped for these PUGs — fixing this surfaces a clean program to re-validate.
        unconfiguredPugs.forEach((pug) => {
          this.createValidationError(
            this.program,
            null,
            `The '${pug.name}' Program User Group has neither an allowance limit nor any applied issuance limits. Every active program requires at least one of these for every Program User Group it serves. Add an allowance or one or more issuance limits on this Program User Group to proceed.`,
            'AllowanceOrIssuanceReq',
          );
        });

        // Product-tag requirement only applies to intentionally issuance-only PUGs. The earlier
        // `pugsWoAllowances.length > 0` gate would fire on unconfigured PUGs too, producing the
        // misleading "must have product tag" message when the real issue was missing allowance/issuance.
        if (issuanceOnlyPugs.length > 0) {
          if (this.program.programProductTypeConfigs.some((x) => x.productTypeConfig.pricedProductType.programProductTypeTagsDescr == '')) {
            this.createValidationError(
              this.program,
              null,
              'One or more Program User Group(s) is issuance-only.  Therefore, all products must have at least one product tag.',
              'ProdTypeTagReq',
            );
          }
        }

        issuanceOnlyPugs.forEach((pug) => {
          const pis = this.getProgramIssuancesForPug(pug);
          const ais = this.getAccountIssuancesForPug(pug);

          // JM 3/25/2026 — product-centric coverage. Each product must have EXACTLY ONE tag
          // with an issuance limit per issuance-only PUG. Zero = uncovered (no limit prevents abuse).
          // Two+ = ambiguous (issuance logs get written to multiple limits, re-tagging orphans history).
          // See Docs/issuance-limit-logic.md for the prior tag-centric design and why it was wrong.
          const coveredTagIds = new Set<string>();
          for (const pi of pis) {
            coveredTagIds.add(pi.programProductTypeTagId);
          }
          for (const ai of ais) {
            coveredTagIds.add(ai.programProductTypeTagId);
          }

          const uncoveredProducts: string[] = [];
          const ambiguousProducts: string[] = [];

          for (const pptc of this.program.programProductTypeConfigs) {
            const tagMaps = pptc.productTypeConfig.pricedProductType.programProductTypeTagMaps;
            if (tagMaps.length === 0) continue; // caught by ProdTypeTagReq check above
            const coveredCount = tagMaps.filter((tm) => coveredTagIds.has(tm.programProductTypeTagId)).length;
            const productName = pptc.productTypeConfig.pricedProductType.productType.name;
            if (coveredCount === 0) {
              uncoveredProducts.push(productName);
            } else if (coveredCount > 1) {
              ambiguousProducts.push(productName);
            }
          }

          if (uncoveredProducts.length > 0) {
            this.createValidationError(
              this.program,
              null,
              `The '${pug.name}' Program User Group is issuance-only but the following products are not covered by any issuance limit: ${uncoveredProducts.join(', ')}.`,
              'IssuanceCatReq',
            );
          }

          if (ambiguousProducts.length > 0) {
            this.createValidationError(
              this.program,
              null,
              `The '${pug.name}' Program User Group has products covered by more than one issuance limit: ${ambiguousProducts.join(', ')}. Each product must be covered by exactly one issuance limit.`,
              'IssuanceCatAmbig',
            );
          }
        });
      }
      //   if (this.program.programBillingTypeId == ProgramBillingTypeEnum.ProgramBillingAddress) {
      //     if (this.program.billingAccountAddressId == null) {
      //       this.createValidationError(
      //         this.program,
      //         'billingAccountAddressId',
      //         'A Billing Address is required for this Program.',
      //         'BillingAddrReq',
      //       );
      //     } else {
      //       await this.validateAddress(this.program.billingAccountAddress);
      //     }
      //   }

      //   if (this.program.programBillingTypeId == ProgramBillingTypeEnum.ProgramUserGroupBillingAddress) {
      //     for (const atug of this.program.approvalTree.approvalTreeUserGroups) {
      //       const contextPrefix = `Program User Group: ${atug.programUserGroup.name}. `;
      //       if (atug.billingAccountAddressId == null) {
      //         this.createValidationError(
      //           this.program,
      //           'billingAccountAddressId',
      //           contextPrefix +
      //             `This program is setup to use a Program User Group Billing Address, but no billing address has been assigned to this program user group `,
      //           'ProgramUserGroupBillingAddrReq',
      //         );
      //       } else {
      //         await this.validateAddress(atug.billingAccountAddress, contextPrefix);
      //       }
      //     }
      //   }
    }
  }

  async validateAddress(accountAddress: IAddress, contextPrefix = '') {
    if (!accountAddress) return;
    const msg = await this.addressValidationService.validateEntityAddress(accountAddress, false);
    if (msg) {
      this.createValidationError(
        this.program,
        'billingAccountAddressId',
        contextPrefix + 'Invalid Billing Address. ' + msg,
        'InvalidBillingAddr',
      );
    }

    const emailRequired = this.account.supplierAccounts.some(
      (x) => x.invoiceDocSubmitTypeId === DocSubmitTypeEnum.Email || x.invoiceDocSubmitTypeId === DocSubmitTypeEnum.PrintAndEmail,
    );
    if (emailRequired && !accountAddress.invoiceEmail) {
      this.createValidationError(
        this.program,
        'billingAccountAddressId',
        contextPrefix + `An Invoice E-Mail address is required when any supplier account's Submit Invoices and Manifests Via is Email or Print and Email`,
        'InvoiceEmailReq',
      );
    }
  }

  // each pug can only have at most one allowance.
  getProgramAllowanceForPug(pug: ProgramUserGroup) {
    const pas = this.program.programAllowances;
    const pa = pas.find((x) => x.programAllowanceUserGroupMaps.some((y) => y.programUserGroupId == pug.id));
    return pa;
  }

  // each pug can have multiple issuances - one per product type tag.
  getProgramIssuancesForPug(pug: ProgramUserGroup) {
    const pis = this.program.programIssuances;
    const pugPis = pis.filter((x) => x.programIssuanceUserGroupMaps.some((y) => y.programUserGroupId == pug.id));
    return pugPis;
  }

  // Jeff added 9/16
  getAccountIssuancesForPug(pug: ProgramUserGroup) {
    const ais = this.program.programAccountIssuanceMaps.map((m) => m.accountIssuance);
    const pugAis = ais.filter((x) => x.accountIssuanceUserGroupMaps.some((y) => y.programUserGroupId == pug.id));
    return pugAis;
  }

  getProgramUserGroupsWithoutAllowances() {
    const allowances = this.program.programAllowances;
    const pugs = this.program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup);
    const pugsWithAllowances = _.flatten(allowances.map((x) => x.programAllowanceUserGroupMaps.map((y) => y.programUserGroup)));
    const pugsWithAllowancesSet = new Set(pugsWithAllowances);
    const pugsWithoutAllowances = pugs.filter((x) => !pugsWithAllowancesSet.has(x));
    return pugsWithoutAllowances;
  }

  getInvalidProgramIssuances() {
    const pIssuances = this.program.programIssuances;
    const pugs = this.program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup);
    const invalidPIssuances = pIssuances.filter((iss) => {
      const usedPugIds = new Set(
        _.flatten(
          pIssuances
            // all issuances with same tag as this one
            .filter((x) => x.programProductTypeTagId == iss.programProductTypeTagId)
            .map((x) => x.programIssuanceUserGroupMaps.map((x) => x.programUserGroupId)),
        ),
      );
      const availablePugs = pugs.filter((x) => !usedPugIds.has(x.id));
      return availablePugs.length > 0;
    });
    return invalidPIssuances;
  }

  // for now called from ProgramSuppliers page.
  public async createMissingProgramSuppliers(save = true) {
    const suppliers = _.uniq(this.program.programProductTypeConfigs.map((x) => x.productTypeConfig.pricedProductType.productType.supplier));
    const missingSuppliers = suppliers.filter((x) => !this.program.programSuppliers.some((y) => y.supplierId == x.id));

    for (const supplier of missingSuppliers) {
      this.dbSaveService.createEntity(ProgramSupplier, {
        id: Guid.create().toString(),
        programId: this.program.id,
        supplierId: supplier.id,
        doesAccountPayFreight: false,
        shouldChargeFreightOnFirstShipmentOnly: false,
        handlingChargePerOrderAmt: 0,
        handlingChargePerShipmentAmt: 0,
      });
    }

    if (save && missingSuppliers.length > 0) {
      await this.dbSaveService.saveChanges();
    }

    return this.program.programSuppliers;
  }

  /**
   * Does this Program's CURRENT configuration require an Organization Tree dedicated to it?
   *
   * Two different reasons:
   *
   *   - Per-PUG purchase orders or p-cards are STORED on ApprovalTreeUserGroup, which every Program
   *     on the tree shares, so setting one Program's would re-route another's orders.
   *   - Collect, Organize and Place AT ANY SCOPE, because the tree DEFINES THE BATCH. "All orders
   *     for this Program" means orders from users in the groups on its tree; on a shared tree
   *     another administrator can restructure those groups mid-campaign and the batch shifts
   *     underneath whoever is reviewing it.
   *
   * Deliberately reads the whole form rather than one field. The designation is offered from
   * several places, and any single trigger can be reversed while another still needs it.
   *
   * Mirrors Program.RequiresProgramSpecificTree() on the server. Keep them in step.
   */
  private requiresProgramSpecificTree() {
    return (
      this.program.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup ||
      this.program.programPcardTypeId === ProgramPcardTypeEnum.ByProgramUserGroup ||
      this.program.collectAndPlace
    );
  }

  /**
   * Withdraw a designation THIS EDITING SESSION made if the form no longer needs it.
   *
   * The promote prompt fires per-field, so an administrator can set purchase orders to per-group,
   * accept the offer, then change their mind — leaving a tree dedicated for a reason that no
   * longer exists, quietly out of the shared pool. Evaluating the whole form here is the only
   * place that can see the final state.
   *
   * Only reverts what we set: the check requires a pending Breeze change from false, so a tree
   * that was ALREADY program-specific before this session — someone else's configuration, with
   * real purchase orders on it — is never touched.
   */
  private reconcileTreeDesignation() {
    const tree = this.program.approvalTree;
    if (!tree?.isProgramSpecific) return;

    const original = tree.entityAspect?.originalValues as Record<string, unknown> | undefined;
    const weDesignatedItThisSession = !!original && 'isProgramSpecific' in original && original['isProgramSpecific'] === false;

    if (weDesignatedItThisSession && !this.requiresProgramSpecificTree()) {
      tree.isProgramSpecific = false;
    }
  }

  override async beforeSave() {
    if (this.isBeingAdded) {
      this.program.entityAspect.setAdded();
    }
    this.reconcileTreeDesignation();
    if (this.guidelinesSub && !(await this.guidelinesSub.beforeSave())) return false;
    await this.createMissingProgramSuppliers(false);
    return true;
  }

  override async afterSave() {
    this.isBeingAdded = false;

    this.refreshAllSubs();
  }

  override async afterUndo() {
    // Forcing a reload of the component is simplest way to avoid side effects.
    // browser reload - works

    // below doesn't work after saving a new program
    //window.location.reload();

    this.router.navigate(['account/accounts', this.accountId, 'manage', 'programs', this.program.id]).then(() => {
      window.location.reload();
    });
  }

  override navigateToValidationError(ee: EntityError) {
    const errEnt = ee.entity;
    const propName = ee.propertyName;

    let viewEnum = ViewEnum.None;

    if (errEnt instanceof ProgramProductTypeConfig) {
      viewEnum = ViewEnum.ProductTypeConfigs;
    } else if (errEnt instanceof ProgramProductTypeTag || errEnt instanceof ProgramProductCategoryTag) {
      viewEnum = ViewEnum.ProductTags;
    } else if (errEnt instanceof ProgramView) {
      viewEnum = ViewEnum.ProductViews;
    } else if (errEnt instanceof ProgramRapidTemplate) {
      viewEnum = ViewEnum.RapidTemplates;
    } else if (errEnt instanceof ProgramIssuance) {
      viewEnum = ViewEnum.Issuances;
    } else if (errEnt instanceof ProgramAllowance) {
      viewEnum = ViewEnum.Allowances;
    } else if (errEnt instanceof ProgramBudget) {
      viewEnum = ViewEnum.Budgets;
    } else if (errEnt instanceof ProgramSupplier) {
      viewEnum = ViewEnum.Suppliers;
    } else if (errEnt instanceof Program) {
      if (propName == 'billingAccountAddressId') {
        viewEnum = ViewEnum.Billing;
      } else {
        viewEnum = ViewEnum.Info;
      }
    }

    if (viewEnum != ViewEnum.None) {
      this.navigateToErrorAsync(viewEnum, errEnt, propName);
    } else {
      UtilFns.focusInputByEntity('#topLevel', errEnt, propName);
    }
  }

  async navigateToErrorAsync(viewEnum: ViewEnum, errEnt: Entity, propName: string) {
    this.activeView = viewEnum;
    await UtilFns.wait(1);
    const viewName = ViewEnum[viewEnum];
    const activeComp = this.componentNameMap[viewName];
    const fn = activeComp?.markError?.bind(activeComp);
    if (fn) {
      fn(errEnt, propName);
    }
  }

  onGotoApprovalTree(approvalTree: ApprovalTree) {
    if (!approvalTree) {
      this.toastr.warning('Select a Proximity Account Organzation Tree first', ' Unable to Proceed');
      return;
    }

    this.router.navigate(['account/accounts', this.accountId, 'manage', 'approval-trees'], { queryParams: { key: approvalTree.id } });
  }

  onGotoBlanketPo() {
    // this.router.navigate(['account/accounts', this.accountId, 'manage', 'approval-trees'], { queryParams: { key: approvalTree.id } });
  }

  async onEditAdminUserGroup(atug: ApprovalTreeUserGroup) {
    //this.router.navigate(['account/accounts', this.accountId, 'manage', 'program-user-groups'], { queryParams: { key: aug.id } });

    // Refuse edit outright for Inactive PUGs — including those deactivated by the PUG
    // Consolidation utility, which stamps ConsolidatedIntoPugId. The dialog is not
    // useful (read-only OR editable) for a PUG that shouldn't be receiving members
    // anymore; new orders route through the winner. Do NOT surface a reactivation
    // path in this message — reactivating a consolidated loser breaks the tool's
    // invariant and would just make things worse.
    const pug = atug.programUserGroup;
    if (pug?.activeStatusId === ActiveStatusEnum.Inactive) {
      await this.dialogService.okDialog(
        'Cannot Edit',
        'This Program User Group is Inactive and cannot be edited.',
      );
      return;
    }

    const activePrograms = await this.dbQueryService.getActiveProgramsForApprovalTree(atug.approvalTreeId);
    const ap = activePrograms.map((x) => {
      return x.name;
    });
    if (activePrograms.length > 0 && this.program.activeStatusId != ActiveStatusEnum.Active) {
      await this.dialogService.okDialog(
        'Cannot Edit',
        'The following program(s) must be HELD before this Program User Group can be edited:<br><br>' + ap.join('<br>'),
      );
    }

    const ok = await ApprovalTreeUserGroupDialogComponent.openDialog(this.pngDialogService, {
      parent: this,
      atug: atug,
      isEditable: activePrograms.length == 0,
      isInventoryProgram: this.program.programTypeId === ProgramTypeEnum.Inventory,
    });

    if (ok) {
      this.refreshAllSubs();
    }
  }

  setCancelIcon() {
    if (!this.hasChanges()) {
      return 'pi pi-backward';
    }
    return 'pi pi-times';
  }

  setCancelLabel(): string {
    if (!this.hasChanges()) {
      return ' Go Back';
    }
    return ' Cancel and Go Back';
  }

  setActivateLabel(): string {
    if (this.hasChanges()) {
      return ' Save & Activate Program';
    }
    return ' Activate Program';
  }

  /** Override showHelp to pass the appropriate anchor based on the active view */
  override showHelp(title?: string, field?: string): void {
    const anchor = this.getAnchorForActiveView();
    console.log('ProgramFrmComponent.showHelp called, activeView:', ViewEnum[this.activeView], 'anchor:', anchor);
    this.dialogService.helpDialog(title || '', this, field, this.navigateToValidationError, undefined, anchor);
  }

  /** Map ViewEnum to anchor IDs in the help fragment */
  private getAnchorForActiveView(): string | undefined {
    switch (this.activeView) {
      case ViewEnum.Info:
        return 'information';
      case ViewEnum.Suppliers:
        return 'supplier-settings';
      case ViewEnum.UserGroups:
        return 'program-user-groups';
      case ViewEnum.ProductTypeConfigs:
        return 'products-and-tags';
      case ViewEnum.Kits:
        return 'kits';
      case ViewEnum.ProductTags:
        return 'menu-categories';
      case ViewEnum.Issuances:
        return 'issuance-limits';
      case ViewEnum.Allowances:
        return 'allowances';
      case ViewEnum.RapidTemplates:
        return 'rapid-orders';
      case ViewEnum.Budgets:
        return 'budgets';
      case ViewEnum.Billing:
        return 'billing';
      default:
        return undefined;
    }
  }
}
